export type PermissionGroupId =
  | 'tour-planning'
  | 'field-execution'
  | 'document-management'
  | 'workflows'
  | 'comments'
  | 'custom-objects'
  | 'workspace-settings'
  | 'billing'
  | 'insights'
  | 'users-service-accounts';

export type PermissionLevel = 'none' | 'read' | 'write' | 'manage';
export type GrantLevel = Exclude<PermissionLevel, 'none'>;
export type PermissionGroupDisplayLevel = PermissionLevel | 'advanced';

export interface PermissionGroupDefinition {
  readonly id: PermissionGroupId;
  readonly titleKey: string;
  readonly descriptionKey: string;
  readonly icon: string;
  readonly levels: readonly PermissionLevel[];
  readonly grantsByLevel: Readonly<Partial<Record<GrantLevel, readonly string[]>>>;
}

export interface GrantMetadata {
  readonly groupId: PermissionGroupId;
  readonly level: GrantLevel;
  readonly label: string;
  readonly description: string;
}

export const ROLE_PERMISSION_GROUPS: readonly PermissionGroupDefinition[] = [
  {
    id: 'tour-planning',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.TOUR_PLANNING.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.TOUR_PLANNING.DESCRIPTION',
    icon: 'pi pi-map',
    levels: ['none', 'read', 'write'],
    grantsByLevel: {
      read: [
        'cases:read',
        'cases:assets:read',
        'bw:external-cases:read',
        'tours:read',
        'vehicles:read',
        'devices:read',
        'areas:read',
        'locations:read',
        'calendars:read',
        'addresses:read',
        'geocoding:read',
      ],
      write: [
        'cases:create',
        'cases:update',
        'cases:assets:create',
        'cases:emails:create',
        'tours:create',
        'tours:update',
        'offerings:create',
        'offerings:update',
        'vehicles:create',
        'vehicles:update',
        'devices:update',
        'areas:create',
        'areas:update',
        'locations:update',
        'calendars:create',
        'calendars:update',
        'calendars:events:create',
        'calendars:events:update',
        'addresses:create',
        'addresses:update',
        'jobs:delete',
        'cases:assets:delete',
        'areas:delete',
        'calendars:events:delete',
        'addresses:delete',
      ],
      manage: [],
    },
  },
  {
    id: 'field-execution',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.FIELD_EXECUTION.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.FIELD_EXECUTION.DESCRIPTION',
    icon: 'pi pi-truck',
    levels: ['none', 'read', 'write'],
    grantsByLevel: {
      read: [
        'cases:read',
        'cases:assets:read',
        'tours:read',
        'vehicles:read',
        'users:read',
        'skills:read',
      ],
      write: [
        'cases:update',
        'cases:assets:create',
        'cases:assets:delete',
        'cases:emails:create',
        'tours:create',
        'tours:update',
        'offerings:update',
        'bw:activities:load:update',
        'bw:activities:delivery:update',
        'bw:activities:police:update',
        'locations:update',
        'devices:update',
        'calendars:events:create',
        'calendars:events:delete',
      ],
    },
  },
  {
    id: 'document-management',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.DOCUMENT_MANAGEMENT.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.DOCUMENT_MANAGEMENT.DESCRIPTION',
    icon: 'pi pi-file',
    levels: ['none', 'read', 'write', 'manage'],
    grantsByLevel: {
      read: ['documents:read'],
      write: ['documents:create', 'documents:update', 'documents:delete'],
      manage: ['documents:settings:update'],
    },
  },
  {
    id: 'workflows',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.WORKFLOWS.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.WORKFLOWS.DESCRIPTION',
    icon: 'pi pi-sitemap',
    levels: ['none', 'manage'],
    grantsByLevel: {
      manage: ['workflows:read', 'workflows:create', 'workflows:update', 'workflows:delete'],
    },
  },
  {
    id: 'comments',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.COMMENTS.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.COMMENTS.DESCRIPTION',
    icon: 'pi pi-comments',
    levels: ['none', 'read', 'write'],
    grantsByLevel: {
      read: ['comments:read'],
      write: ['comments:create', 'comments:update', 'comments:delete'],
      manage: [],
    },
  },
  {
    id: 'custom-objects',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.CUSTOM_OBJECTS.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.CUSTOM_OBJECTS.DESCRIPTION',
    icon: 'pi pi-table',
    levels: ['none', 'read', 'write', 'manage'],
    grantsByLevel: {
      read: ['custom-objects:definitions:read'],
      write: ['custom-objects:instances:create', 'custom-objects:instances:update'],
      manage: [
        'custom-objects:definitions:create',
        'custom-objects:definitions:update',
        'custom-objects:instances:delete',
        'saved-views:publications:create',
        'saved-views:publications:update',
      ],
    },
  },
  {
    id: 'workspace-settings',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.WORKSPACE_SETTINGS.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.WORKSPACE_SETTINGS.DESCRIPTION',
    icon: 'pi pi-cog',
    levels: ['none', 'read', 'write', 'manage'],
    grantsByLevel: {
      read: [
        'brands:read',
        'webhooks:read',
        'geocoding:read',
        'mcp:tools:read',
        'skills:read',
        'tags:read',
        'push-notifications:read',
      ],
      write: [
        'sms:deliveries:create',
        'mcp:tools:update',
        'tags:create',
        'tags:update',
        'push-notifications:create',
      ],
      manage: [
        'tenant:settings:update',
        'sms:settings:create',
        'sms:settings:update',
        'sms:settings:delete',
        'sms:reports:read',
        'customDomains:manage',
        'geocoding:settings:update',
        'mcp:settings:update',
        'ai-assistant:settings:update',
        'webhooks:create',
        'webhooks:update',
        'brands:create',
        'brands:update',
        'skills:create',
        'skills:update',
        'skills:delete',
      ],
    },
  },
  {
    id: 'billing',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.BILLING.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.BILLING.DESCRIPTION',
    icon: 'pi pi-credit-card',
    levels: ['none', 'read', 'manage'],
    grantsByLevel: {
      read: ['billing:read'],
      manage: ['billing:update'],
    },
  },
  {
    id: 'insights',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.INSIGHTS.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.INSIGHTS.DESCRIPTION',
    icon: 'pi pi-chart-line',
    levels: ['none', 'read', 'write'],
    grantsByLevel: {
      read: ['dashboard:read', 'messages:read', 'audit-logs:read', 'deleted-events:read'],
      write: ['messages:update'],
    },
  },
  {
    id: 'users-service-accounts',
    titleKey: 'SETTINGS_ACCESS.ROLES.GROUPS.USERS_SERVICE_ACCOUNTS.TITLE',
    descriptionKey: 'SETTINGS_ACCESS.ROLES.GROUPS.USERS_SERVICE_ACCOUNTS.DESCRIPTION',
    icon: 'pi pi-users',
    levels: ['none', 'read', 'write', 'manage'],
    grantsByLevel: {
      read: ['users:read', 'roles:read', 'service-accounts:read'],
      write: ['users:create', 'users:update', 'service-accounts:tokens:create'],
      manage: [
        'users:delete',
        'roles:create',
        'roles:update',
        'roles:delete',
        'service-accounts:create',
        'service-accounts:tokens:delete',
      ],
    },
  },
];

export const ROLE_PERMISSION_GROUP_BY_ID = Object.fromEntries(
  ROLE_PERMISSION_GROUPS.map((group) => [group.id, group])
) as Readonly<Record<PermissionGroupId, PermissionGroupDefinition>>;

const LEVEL_ORDER: readonly PermissionLevel[] = ['none', 'read', 'write', 'manage'];
const GRANT_LEVEL_ORDER: readonly GrantLevel[] = ['read', 'write', 'manage'];

export const ALL_GROUP_LEVELS_NONE = Object.fromEntries(
  ROLE_PERMISSION_GROUPS.map((group) => [group.id, 'none'])
) as Record<PermissionGroupId, PermissionLevel>;

export function grantsForGroupLevel(
  group: PermissionGroupDefinition,
  level: PermissionLevel
): readonly string[] {
  if (level === 'none') {
    return [];
  }
  const levelIndex = LEVEL_ORDER.indexOf(level);
  const grants = new Set<string>();
  for (const grantLevel of GRANT_LEVEL_ORDER) {
    if (LEVEL_ORDER.indexOf(grantLevel) <= levelIndex) {
      group.grantsByLevel[grantLevel]?.forEach((grant) => grants.add(grant));
    }
  }
  return [...grants].sort();
}

export function allGrantsForGroup(group: PermissionGroupDefinition): readonly string[] {
  return grantsForGroupLevel(group, 'manage');
}

export function grantsForGroupFromGrantList(
  group: PermissionGroupDefinition,
  grants: readonly string[]
): readonly string[] {
  const groupGrants = new Set(allGrantsForGroup(group));
  return grants.filter((grant) => groupGrants.has(grant)).sort((a, b) => a.localeCompare(b));
}

export function resolveHighestGroupLevel(
  group: PermissionGroupDefinition,
  grants: readonly string[]
): PermissionLevel {
  const grantSet = new Set(grants);
  const supportedLevels = [...group.levels].filter((level) => level !== 'none').reverse();
  for (const level of supportedLevels) {
    const levelGrants = group.grantsByLevel[level as GrantLevel] ?? [];
    if (levelGrants.some((grant) => grantSet.has(grant))) {
      return level;
    }
  }
  return 'none';
}

export function resolveGroupLevel(
  group: PermissionGroupDefinition,
  grantSet: ReadonlySet<string>
): PermissionLevel {
  const supportedLevels = [...group.levels].filter((level) => level !== 'none').reverse();
  for (const level of supportedLevels) {
    const grants = grantsForGroupLevel(group, level);
    if (grants.length > 0 && grants.every((grant) => grantSet.has(grant))) {
      return level;
    }
  }
  return 'none';
}

export function resolveExactGroupLevel(
  group: PermissionGroupDefinition,
  grantSet: ReadonlySet<string>
): PermissionLevel | null {
  const selectedGroupGrants = new Set(
    allGrantsForGroup(group).filter((grant) => grantSet.has(grant))
  );

  for (const level of group.levels) {
    const levelGrants = grantsForGroupLevel(group, level);
    if (
      levelGrants.length === selectedGroupGrants.size &&
      levelGrants.every((grant) => selectedGroupGrants.has(grant))
    ) {
      return level;
    }
  }

  return null;
}

export function replaceGroupLevelInGrantSet(
  targetGroup: PermissionGroupDefinition,
  level: PermissionLevel,
  grantSet: ReadonlySet<string>,
  groupDisplayLevels: Readonly<Record<PermissionGroupId, PermissionGroupDisplayLevel>>,
  groups: readonly PermissionGroupDefinition[] = ROLE_PERMISSION_GROUPS
): Set<string> {
  if (!targetGroup.levels.includes(level)) {
    return new Set(grantSet);
  }

  const targetGrants = new Set(allGrantsForGroup(targetGroup));
  const replacementGrants = new Set(grantsForGroupLevel(targetGroup, level));
  const preservedSharedGrants = sharedGrantsPreservedByOtherGroups(
    targetGroup,
    targetGrants,
    grantSet,
    groupDisplayLevels,
    groups
  );
  const next = new Set(grantSet);

  for (const grant of targetGrants) {
    if (!replacementGrants.has(grant) && !preservedSharedGrants.has(grant)) {
      next.delete(grant);
    }
  }

  for (const grant of replacementGrants) {
    next.add(grant);
  }

  return next;
}

export function buildGroupLevelsFromGrants(
  grants: readonly string[]
): Record<PermissionGroupId, PermissionLevel> {
  const grantSet = new Set(grants);
  return Object.fromEntries(
    ROLE_PERMISSION_GROUPS.map((group) => [group.id, resolveGroupLevel(group, grantSet)])
  ) as Record<PermissionGroupId, PermissionLevel>;
}

export function buildGroupDisplayLevelsFromGrants(
  grants: readonly string[]
): Record<PermissionGroupId, PermissionGroupDisplayLevel> {
  const grantSet = new Set(grants);
  const candidateLevels = Object.fromEntries(
    ROLE_PERMISSION_GROUPS.map((group) => [group.id, resolveGroupLevel(group, grantSet)])
  ) as Record<PermissionGroupId, PermissionLevel>;
  const explainedGrants = new Set<string>();

  for (const group of ROLE_PERMISSION_GROUPS) {
    grantsForGroupLevel(group, candidateLevels[group.id]).forEach((grant) =>
      explainedGrants.add(grant)
    );
  }

  return Object.fromEntries(
    ROLE_PERMISSION_GROUPS.map((group) => {
      const exactLevel = resolveExactGroupLevel(group, grantSet);
      if (exactLevel !== null) {
        return [group.id, exactLevel];
      }

      const hasUnexplainedOwnedGrant = allGrantsForGroup(group).some(
        (grant) =>
          grantSet.has(grant) &&
          !explainedGrants.has(grant) &&
          metadataForGrant(grant).groupId === group.id
      );
      return [group.id, hasUnexplainedOwnedGrant ? 'advanced' : candidateLevels[group.id]];
    })
  ) as Record<PermissionGroupId, PermissionGroupDisplayLevel>;
}

export function reconcileGroupDisplayLevelsAfterGrantToggle(
  grant: string,
  enabled: boolean,
  selectedGrants: ReadonlySet<string>,
  currentLevels: Readonly<Record<PermissionGroupId, PermissionGroupDisplayLevel>>,
  groups: readonly PermissionGroupDefinition[] = ROLE_PERMISSION_GROUPS
): Record<PermissionGroupId, PermissionGroupDisplayLevel> {
  const changedGrantGroupId = metadataForGrant(grant).groupId;
  const next = { ...currentLevels };

  for (const group of groups) {
    if (!allGrantsForGroup(group).includes(grant)) {
      continue;
    }

    const currentLevel = next[group.id];
    const exactLevel = resolveExactGroupLevel(group, selectedGrants);
    if (exactLevel === currentLevel) {
      continue;
    }

    if (currentLevel === 'advanced') {
      const hasSelectedGroupGrants = allGrantsForGroup(group).some((groupGrant) =>
        selectedGrants.has(groupGrant)
      );
      next[group.id] = exactLevel ?? (hasSelectedGroupGrants ? 'advanced' : 'none');
      continue;
    }

    const currentLevelGrants = new Set(grantsForGroupLevel(group, currentLevel));
    const changedPrimaryGroup = changedGrantGroupId === group.id;
    const removedRequiredGrant = !enabled && currentLevelGrants.has(grant);
    const addedPrimaryExtraGrant =
      enabled && changedPrimaryGroup && !currentLevelGrants.has(grant);
    if (removedRequiredGrant || addedPrimaryExtraGrant) {
      next[group.id] = exactLevel ?? 'advanced';
    }
  }

  return next;
}

export function buildGrantsFromGroupLevels(
  levels: Readonly<Record<PermissionGroupId, PermissionLevel>>
): Set<string> {
  const grants = new Set<string>();
  for (const group of ROLE_PERMISSION_GROUPS) {
    grantsForGroupLevel(group, levels[group.id]).forEach((grant) => grants.add(grant));
  }
  return grants;
}

export function grantLevelInGroup(group: PermissionGroupDefinition, grant: string): GrantLevel {
  for (const level of GRANT_LEVEL_ORDER) {
    if (group.grantsByLevel[level]?.includes(grant)) {
      return level;
    }
  }
  return 'read';
}

function sharedGrantsPreservedByOtherGroups(
  targetGroup: PermissionGroupDefinition,
  targetGrants: ReadonlySet<string>,
  grantSet: ReadonlySet<string>,
  groupDisplayLevels: Readonly<Record<PermissionGroupId, PermissionGroupDisplayLevel>>,
  groups: readonly PermissionGroupDefinition[]
): Set<string> {
  const preserved = new Set<string>();

  for (const group of groups) {
    if (group.id === targetGroup.id) {
      continue;
    }

    const enabledOverlappingGrants = allGrantsForGroup(group).filter(
      (grant) => targetGrants.has(grant) && grantSet.has(grant)
    );
    if (enabledOverlappingGrants.length === 0) {
      continue;
    }

    const displayLevel = groupDisplayLevels[group.id];
    if (displayLevel === 'advanced') {
      enabledOverlappingGrants.forEach((grant) => preserved.add(grant));
      continue;
    }

    const requiredByExactLevel = new Set(grantsForGroupLevel(group, displayLevel));
    enabledOverlappingGrants
      .filter((grant) => requiredByExactLevel.has(grant))
      .forEach((grant) => preserved.add(grant));
  }

  return preserved;
}

export function metadataForGrant(grant: string): GrantMetadata {
  const group = ROLE_PERMISSION_GROUPS.find((candidate) =>
    allGrantsForGroup(candidate).includes(grant)
  );
  const resolvedGroup = group ?? fallbackPermissionGroup();
  const copy = GRANT_COPY[grant] ?? createFallbackGrantCopy(grant);

  return {
    groupId: resolvedGroup.id,
    level: grantLevelInGroup(resolvedGroup, grant),
    label: copy.label,
    description: copy.description,
  };
}

function fallbackPermissionGroup(): PermissionGroupDefinition {
  const group = ROLE_PERMISSION_GROUPS[0];
  if (!group) {
    throw new Error('At least one role permission group is required.');
  }
  return group;
}

const GRANT_COPY: Readonly<
  Record<string, { readonly label: string; readonly description: string }>
> = {
  'addresses:create': {
    label: 'Create addresses',
    description: 'Add stored addresses and address configuration records.',
  },
  'addresses:delete': {
    label: 'Delete addresses',
    description: 'Remove stored addresses or address configuration records.',
  },
  'addresses:read': {
    label: 'View addresses',
    description: 'Read stored addresses and address configuration.',
  },
  'addresses:update': {
    label: 'Update addresses',
    description: 'Edit stored addresses and address configuration.',
  },
  'ai-assistant:settings:update': {
    label: 'Manage AI settings',
    description: 'Change AI assistant workspace settings.',
  },
  'areas:create': {
    label: 'Create areas',
    description: 'Define a new service area.',
  },
  'areas:delete': {
    label: 'Delete areas',
    description: 'Permanently remove a service area.',
  },
  'areas:read': {
    label: 'View areas',
    description: 'Read geographic service areas.',
  },
  'areas:update': {
    label: 'Update areas',
    description: 'Edit area boundaries or settings.',
  },
  'audit-logs:read': {
    label: 'View audit logs',
    description: 'Read security and activity audit events.',
  },
  'billing:read': {
    label: 'View billing',
    description: 'Read billing plans, invoices, and subscription status.',
  },
  'billing:update': {
    label: 'Manage billing',
    description: 'Change billing subscriptions and plan settings.',
  },
  'brands:create': {
    label: 'Create brands',
    description: 'Add brands for workspace communication and presentation.',
  },
  'brands:read': {
    label: 'View brands',
    description: 'Read brand configuration.',
  },
  'brands:update': {
    label: 'Update brands',
    description: 'Edit brand configuration.',
  },
  'bw:activities:delivery:update': {
    label: 'Record delivery activities',
    description: 'Update BW delivery activity progress from field work.',
  },
  'bw:activities:load:update': {
    label: 'Record load activities',
    description: 'Update BW load activity progress from field work.',
  },
  'bw:activities:police:update': {
    label: 'Record police activities',
    description: 'Update BW police activity progress from field work.',
  },
  'bw:external-cases:read': {
    label: 'View external BW cases',
    description: 'Read BW external case ingest records.',
  },
  'calendars:create': {
    label: 'Create calendars',
    description: 'Add a shared calendar.',
  },
  'calendars:events:create': {
    label: 'Create calendar events',
    description: 'Add events to shared calendars.',
  },
  'calendars:events:delete': {
    label: 'Delete calendar events',
    description: 'Remove shared calendar events permanently.',
  },
  'calendars:events:update': {
    label: 'Update calendar events',
    description: 'Edit shared calendar events.',
  },
  'calendars:read': {
    label: 'View calendars',
    description: 'Read shared calendars and their events.',
  },
  'calendars:update': {
    label: 'Update calendars',
    description: 'Edit shared calendar settings.',
  },
  'cases:assets:create': {
    label: 'Attach case files',
    description: 'Upload files and evidence to cases.',
  },
  'cases:assets:delete': {
    label: 'Delete case files',
    description: 'Remove files and evidence from cases.',
  },
  'cases:assets:read': {
    label: 'View case files',
    description: 'Open files and evidence attached to cases.',
  },
  'cases:create': {
    label: 'Create cases',
    description: 'Create new cases in the workspace.',
  },
  'cases:emails:create': {
    label: 'Send case emails',
    description: 'Create outbound emails related to a case.',
  },
  'cases:read': {
    label: 'View cases',
    description: 'Read cases and their details.',
  },
  'cases:update': {
    label: 'Update cases',
    description: 'Edit case details and status.',
  },
  'comments:create': {
    label: 'Create comments',
    description: 'Post threaded comments on visible records.',
  },
  'comments:delete': {
    label: 'Delete comments',
    description: 'Moderate or remove comments from other users.',
  },
  'comments:read': {
    label: 'View comments',
    description: 'Read threaded comments on visible records.',
  },
  'comments:update': {
    label: 'Moderate comments',
    description: 'Edit, restore, and inspect history for comments from other users.',
  },
  'custom-objects:definitions:create': {
    label: 'Create object definitions',
    description: 'Create custom object schemas.',
  },
  'custom-objects:definitions:read': {
    label: 'View object definitions',
    description: 'Read custom object schemas.',
  },
  'custom-objects:definitions:update': {
    label: 'Update object definitions',
    description: 'Edit custom object schemas.',
  },
  'custom-objects:instances:create': {
    label: 'Create object records',
    description: 'Create records for custom objects.',
  },
  'custom-objects:instances:delete': {
    label: 'Delete object records',
    description: 'Delete records for custom objects.',
  },
  'custom-objects:instances:update': {
    label: 'Update object records',
    description: 'Edit records for custom objects.',
  },
  'customDomains:manage': {
    label: 'Manage custom domains',
    description: 'Configure workspace custom domains.',
  },
  'dashboard:read': {
    label: 'View dashboard',
    description: 'Read workspace dashboard information.',
  },
  'deleted-events:read': {
    label: 'View deleted events',
    description: 'Inspect records of deleted events.',
  },
  'devices:create': {
    label: 'Create devices',
    description: 'Register new mobile or field devices.',
  },
  'devices:read': {
    label: 'View devices',
    description: 'Read device records.',
  },
  'devices:update': {
    label: 'Update devices',
    description: 'Edit device records or assignments.',
  },
  'documents:create': {
    label: 'Create documents',
    description: 'Upload or create managed documents.',
  },
  'documents:delete': {
    label: 'Delete documents',
    description: 'Remove managed documents.',
  },
  'documents:read': {
    label: 'View documents',
    description: 'Open and search managed documents.',
  },
  'documents:settings:update': {
    label: 'Manage document settings',
    description: 'Change document retention and management settings.',
  },
  'documents:update': {
    label: 'Update documents',
    description: 'Edit managed document metadata or content.',
  },
  'geocoding:read': {
    label: 'Use geocoding',
    description: 'Run address lookup and reverse geocoding.',
  },
  'geocoding:settings:update': {
    label: 'Manage geocoding settings',
    description: 'Change geocoding provider and workspace lookup settings.',
  },
  'jobs:delete': {
    label: 'Delete jobs',
    description: 'Cancel or permanently remove jobs.',
  },
  'locations:read': {
    label: 'View locations',
    description: 'Read live or stored location information.',
  },
  'locations:update': {
    label: 'Update locations',
    description: 'Record or edit location information.',
  },
  'mcp:settings:update': {
    label: 'Manage MCP settings',
    description: 'Configure MCP server settings.',
  },
  'mcp:tools:read': {
    label: 'Use read-only MCP tools',
    description: 'Run MCP tools that only read data.',
  },
  'mcp:tools:update': {
    label: 'Use write MCP tools',
    description: 'Run MCP tools that can change workspace data.',
  },
  'messages:read': {
    label: 'View messages',
    description: 'Read message and inbox items.',
  },
  'messages:update': {
    label: 'Update messages',
    description: 'Change message status or handling state.',
  },
  'offerings:create': {
    label: 'Create offerings',
    description: 'Create case-to-tour offering records.',
  },
  'offerings:update': {
    label: 'Update offerings',
    description: 'Edit case-to-tour assignments and offerings.',
  },
  'push-notifications:create': {
    label: 'Send test push notifications',
    description: 'Create push-notification test deliveries.',
  },
  'push-notifications:read': {
    label: 'View push notification settings',
    description: 'Read push-notification settings and device tokens.',
  },
  'roles:create': {
    label: 'Create roles',
    description: 'Create custom roles.',
  },
  'roles:delete': {
    label: 'Delete roles',
    description: 'Delete custom roles.',
  },
  'roles:read': {
    label: 'View roles',
    description: 'Read roles and the grant catalog.',
  },
  'roles:update': {
    label: 'Update roles',
    description: 'Edit custom roles and assignment rules.',
  },
  'saved-views:publications:create': {
    label: 'Publish saved views',
    description: 'Create public saved-view publications.',
  },
  'saved-views:publications:update': {
    label: 'Update public saved views',
    description: 'Update public saved-view publication state.',
  },
  'service-accounts:create': {
    label: 'Create service accounts',
    description: 'Create service-account users.',
  },
  'service-accounts:read': {
    label: 'View service accounts',
    description: 'Read service-account users and credentials metadata.',
  },
  'service-accounts:tokens:create': {
    label: 'Create service-account tokens',
    description: 'Create or rotate service-account tokens.',
  },
  'service-accounts:tokens:delete': {
    label: 'Revoke service-account tokens',
    description: 'Delete or blacklist service-account tokens.',
  },
  'skills:create': {
    label: 'Create skills',
    description: 'Create workspace skills.',
  },
  'skills:delete': {
    label: 'Delete skills',
    description: 'Delete workspace skills.',
  },
  'skills:read': {
    label: 'View skills',
    description: 'Read workspace skills.',
  },
  'skills:update': {
    label: 'Update skills',
    description: 'Edit workspace skills.',
  },
  'sms:deliveries:create': {
    label: 'Send SMS deliveries',
    description: 'Create outbound SMS delivery records.',
  },
  'sms:reports:read': {
    label: 'View SMS reports',
    description: 'Read SMS delivery reports.',
  },
  'sms:settings:create': {
    label: 'Create SMS settings',
    description: 'Create SMS gateway settings.',
  },
  'sms:settings:delete': {
    label: 'Delete SMS settings',
    description: 'Remove SMS gateway settings.',
  },
  'sms:settings:update': {
    label: 'Update SMS settings',
    description: 'Edit SMS gateway settings.',
  },
  'tags:create': {
    label: 'Create tags',
    description: 'Create workspace tags.',
  },
  'tags:read': {
    label: 'View tags',
    description: 'Read workspace tags.',
  },
  'tags:update': {
    label: 'Update tags',
    description: 'Edit workspace tags.',
  },
  'tenant:settings:update': {
    label: 'Manage workspace settings',
    description: 'Update tenant-wide workspace settings.',
  },
  'tours:create': {
    label: 'Create tours',
    description: 'Create tours and route plans.',
  },
  'tours:read': {
    label: 'View tours',
    description: 'Read tours and route plans.',
  },
  'tours:update': {
    label: 'Update tours',
    description: 'Edit tours, assignments, and route plans.',
  },
  'users:create': {
    label: 'Create users',
    description: 'Invite or create workspace users.',
  },
  'users:delete': {
    label: 'Delete users',
    description: 'Delete workspace users.',
  },
  'users:read': {
    label: 'View users',
    description: 'Read workspace users.',
  },
  'users:update': {
    label: 'Update users',
    description: 'Edit workspace users and their role assignments.',
  },
  'vehicles:create': {
    label: 'Create vehicles',
    description: 'Create vehicle records.',
  },
  'vehicles:read': {
    label: 'View vehicles',
    description: 'Read vehicle records.',
  },
  'vehicles:update': {
    label: 'Update vehicles',
    description: 'Edit vehicle records.',
  },
  'webhooks:create': {
    label: 'Create webhooks',
    description: 'Create webhook endpoints.',
  },
  'webhooks:read': {
    label: 'View webhooks',
    description: 'Read webhook endpoint configuration.',
  },
  'webhooks:update': {
    label: 'Update webhooks',
    description: 'Edit webhook endpoint configuration.',
  },
  'workflows:create': {
    label: 'Create workflows',
    description: 'Create automation workflows.',
  },
  'workflows:delete': {
    label: 'Delete workflows',
    description: 'Delete automation workflows.',
  },
  'workflows:read': {
    label: 'View workflows',
    description: 'Read automation workflows.',
  },
  'workflows:update': {
    label: 'Update workflows',
    description: 'Edit automation workflows.',
  },
};

function createFallbackGrantCopy(grant: string): {
  readonly label: string;
  readonly description: string;
} {
  const parts = grant.split(':');
  const action = parts.pop() ?? 'access';
  const resource = parts.join(' ');
  const label = `${capitalize(action)} ${humanize(resource)}`.trim();
  return {
    label,
    description: `Allow ${action} access for ${humanize(resource).toLowerCase()}.`,
  };
}

function humanize(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
